/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ProactiveChatNames } from '../../helpers/constants';
import { IFromSpecificPageTriggerContext, TContextProvider } from '../../../interfaces';
import ProactiveChatTrigger from './proactive-chat-trigger';

/**
 * FromSpecificPageTrigger class.
 * Proactive chat is triggered if user has come from specific page (the one listed in config.proactiveOnFromSpecificPageList).
 */
export default class FromSpecificPageTrigger extends ProactiveChatTrigger {
    /**
     * Validates whether the condition(s) is met to trigger proactive chat: the user has come from a specific page.
     * @returns {Promise<boolean>} True if condition is met. Otherwise, false.
     */
    public async validate(): Promise<boolean> {
        let isValid = false;
        if (this.props.config.proactiveOnFromSpecificPageList) {
            this.props.context.telemetry.debug(
                `[${FromSpecificPageTrigger.name}] Number of items in user-defined list of 'From Specific Page' was '${this.props.config.proactiveOnFromSpecificPageList?.length}'.`
            );

            for (let i = 0; i < (this.props.config.proactiveOnFromSpecificPageList?.length || 0); i++) {
                const fromSpecificPageTrimmed: string = this.props.config.proactiveOnFromSpecificPageList[i].trim();

                this.props.context.telemetry.debug(
                    `[Chat][${FromSpecificPageTrigger.name}] Current page URL: '${window.document.referrer}'.`
                );
                this.props.context.telemetry.debug(
                    `[${FromSpecificPageTrigger.name}] Current item from user-defined list of 'From Specific Page': '${fromSpecificPageTrimmed}'.`
                );

                if (window.document.referrer.indexOf(fromSpecificPageTrimmed) > -1) {
                    isValid = true;
                    break;
                }
            }
        } else {
            this.props.context.telemetry.debug(
                `[Chat][${FromSpecificPageTrigger.name}] User-defined 'From Specific Page' list was undefined.`
            );
        }

        this.props.context.telemetry.debug(`[Chat][${FromSpecificPageTrigger.name}] Validation returning '${isValid}'.`);
        return isValid;
    }

    /**
     * Gets the context.
     * @returns {TContextProvider} The context.
     */
    public getContextProvider(): TContextProvider {
        return (): IFromSpecificPageTriggerContext => {
            return {
                ...this.context,
                IsProactiveChat: {
                    value: 'True',
                    isDisplayable: true
                },
                ProactiveType: {
                    value: ProactiveChatNames.FromSpecificPage,
                    isDisplayable: true
                },
                CameFromPage: {
                    value: window.document.referrer,
                    isDisplayable: true
                }
            };
        };
    }

    /**
     * Gets the message to be displayed when proactive chat is triggered.
     * @returns {string} The message to be displayed when proactive chat is triggered.
     */
    public getMessage(): string {
        return this.props.config.proactiveOnFromSpecificPageMessage || this.props.config.proactiveDefaultMessage;
    }
}
